import { useEffect } from 'react';
import { X } from 'lucide-react';

interface ShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);
const mod = isMac ? '\u2318' : 'Ctrl';

const shortcuts = [
  { section: 'Global', items: [
    { keys: `${mod}+K`, description: 'Open search / command palette' },
    { keys: `${mod}+I`, description: 'Quick add item' },
    { keys: `${mod}+J`, description: 'Open AI assistant' },
    { keys: '?', description: 'Show this shortcuts reference' },
    { keys: 'Escape', description: 'Close any open modal or drawer' },
  ]},
  { section: 'Command Palette', items: [
    { keys: '> prefix', description: 'Switch to commands mode' },
    { keys: '\u2191 / \u2193', description: 'Navigate results' },
    { keys: 'Enter', description: 'Select result or execute command' },
  ]},
  { section: 'Cards', items: [
    { keys: 'Click card', description: 'Expand card to full view' },
    { keys: 'Escape', description: 'Close expanded card' },
  ]},
];

export default function ShortcutsModal({ isOpen, onClose }: ShortcutsModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div
        className="card-modal-backdrop"
        style={{ zIndex: 200 }}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Keyboard shortcuts"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(520px, 95vw)',
          maxHeight: '80vh',
          zIndex: 201,
        }}
        className="card-shell"
      >
        <div className="card-header" style={{ marginBottom: '1rem' }}>
          <h2 className="card-title">Keyboard Shortcuts</h2>
          <button
            type="button"
            onClick={onClose}
            className="icon-button"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div style={{ overflowY: 'auto', maxHeight: '60vh' }}>
          {shortcuts.map((group) => (
            <div key={group.section} style={{ marginBottom: '1.25rem' }}>
              <div className="field-label" style={{ marginBottom: '0.5rem' }}>
                {group.section}
              </div>
              {group.items.map((item) => (
                <div
                  key={item.keys}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.4rem 0',
                    borderBottom: '1px solid var(--border-subtle)',
                  }}
                >
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                    {item.description}
                  </span>
                  <kbd className="header-shortcut" style={{ marginLeft: '1rem', whiteSpace: 'nowrap' }}>
                    {item.keys}
                  </kbd>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
